import { useEffect, useMemo, useState } from 'react'
import { useUserContext } from '../../context/UserContext.jsx'
import { FormProvider, useForm } from 'react-hook-form'
import { gql, useMutation, useQuery } from '@apollo/client'
import FormInput from '../../components/atoms/Input/Form-Input'
import Button from '../../components/atoms/Buttons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  LIST_DISCIPLINES,
  LIST_DISCIPLINES_QUERY_OPTIONS,
} from '../../apollo/queries/listDisciplines'
import { CREATE_SADDLE } from '../../apollo/mutations/createSaddle'
import { formatListArray, makeId14, seatSizes } from '../../utils'
import ImagePlus from '../../assets/icons/ImagePlus.svg?react'
import CloseIcon from '../../assets/icons/close-icon.svg?react'
import useSignedUrlUpload from '../../hooks/useSignedUrlUpload'
import { createToast } from '../../utils/Toast'
import { GET_SADDLE_DETAIL_QUERY } from '../../apollo/queries/saddleDetails'
import { BeatLoader } from 'react-spinners'
import VariantsTable from '../../components/VariantsTable'
import ModalComponent from '../../components/atoms/Modal/ModalComponent/ModalComponent'
import { UPDATE_SADDLE } from '../../apollo/mutations/updateSaddle'
import { CREATE_SADDLE_VARIANT } from '../../apollo/mutations/createSaddleVariant'
import { UPDATE_SADDLE_VARIANT } from '../../apollo/mutations/updateSaddleVariant'
import { DELETE_SADDLE } from '../../apollo/mutations/deleteSaddle'
import FormRichTextEditor from '../../components/atoms/Input/FormRichTextEditor'

const Toast = createToast()

function SaddleForm() {
  const [numericId] = useState(makeId14)

  const productId = useMemo(() => `saddles#product#${numericId}`, [numericId])
  console.log('productId', productId)

  const navigate = useNavigate()
  const { encodedId } = useParams()
  const location = useLocation()
  const { userData } = useUserContext()
  /** Products table pk; URL refresh has no location.state — infer from user or leave null (API resolves by product id). */
  const effectiveBrandName = useMemo(() => {
    const fromState = location.state?.brandName
    if (fromState) return fromState
    const b = userData?.brand?.toLowerCase?.()
    if (b === 'cactus') return 'cactussaddlery'
    if (b && b !== 'saddlefitadmin') return userData.brand
    return null
  }, [location.state?.brandName, userData?.brand])
  const brandName = effectiveBrandName
  const isViewMode = location.state?.isViewMode || false
  const isEditMode = !!encodedId
  const saddleId = isEditMode ? decodeURIComponent(encodedId) : null
  const isCreateMode = !isEditMode

  const { uploadFile } = useSignedUrlUpload()
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })

  const handleSort = key => {
    setSortConfig(prevConfig => {
      const direction =
        prevConfig?.key === key && prevConfig?.direction === 'ascending'
          ? 'descending'
          : 'ascending'
      return { key, direction }
    })
  }

  const methods = useForm({
    defaultValues: {
      discipline: '',
      size: '',
      productTitle: '',
      sku: '',
      barAngle: '',
      // gulletHeight: "",
      gulletWidth: '',
      price: 0,
      discount: 0,
      quantity: 1,
      variantPrice: '',
      description: '',
      status: 'SELL_NOT_LIVE',
    },
  })

  const { clearErrors, setValue, handleSubmit, reset, watch } = methods

  const [cancelModal, setCancelModal] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(false)

  const [saddleImages, setSaddleImages] = useState(Array(4).fill(null))
  const [cellImageErrors, setCellImageErrors] = useState(Array(4).fill(''))
  const [imageError, setImageError] = useState('')
  const [galleryTouched, setGalleryTouched] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(Array(4).fill(false))

  const [thumbnail, setThumbnail] = useState(null)
  const [thumbnailError, setThumbnailError] = useState('')
  const [thumbnailTouched, setThumbnailTouched] = useState(false)
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false)

  const [meshFile, setMeshFile] = useState(null)
  const [meshFileName, setMeshFileName] = useState(null)
  const [meshError, setMeshError] = useState('')
  const [meshTouched, setMeshTouched] = useState(false)
  const [uploadingMesh, setUploadingMesh] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [currentlyDeleting, setCurrentlyDeleting] = useState(null)

  useEffect(() => {
    if (openEditModal && selectedVariant) {
      setValue('size', selectedVariant.size)
      setValue('variantPrice', selectedVariant.price)
      setValue('quantity', selectedVariant.quantity)
    }
  }, [openEditModal, selectedVariant, setValue])

  const seatSizeValue = watch('size')
  const selectedSeatSizeOption =
    seatSizes.find(opt => opt.value === seatSizeValue) || null

  const { data: listDisciplinesData } = useQuery(
    gql(LIST_DISCIPLINES),
    LIST_DISCIPLINES_QUERY_OPTIONS,
  )
  const westernOptions = listDisciplinesData?.listDisciplines
    ? formatListArray(listDisciplinesData.listDisciplines).filter(
        option => option.label === 'WESTERN',
      )
    : []

  const [updateSaddle, { loading: updateSaddleLoading }] =
    useMutation(UPDATE_SADDLE)
  const [createSaddle, { loading }] = useMutation(CREATE_SADDLE)
  const [deleteSaddleVariant, { loading: deleteSaddleLoading }] =
    useMutation(DELETE_SADDLE)
  const [createSaddleVariant, { loading: createSaddleVariantLoading }] =
    useMutation(CREATE_SADDLE_VARIANT)
  const [updateSaddleVariant, { loading: updateSaddleVariantLoading }] =
    useMutation(UPDATE_SADDLE_VARIANT)

  const { data, loading: saddleDetailsLoading, error: saddleDetailError } =
    useQuery(GET_SADDLE_DETAIL_QUERY, {
      variables: { id: saddleId, brandName: brandName ?? null },
      skip: !isEditMode,
      onCompleted: data => {
        if (data?.listSaddleDetail && data.listSaddleDetail.length > 0) {
          const rows = data.listSaddleDetail
          const saddle =
            rows.find(s => s.type === 'PRODUCT') ||
            rows.find(s => s.sk && !String(s.sk).includes('#variant#')) ||
            rows[0]

          reset({
            discipline: saddle.discipline,
            size: saddle?.size || '',
            productTitle: saddle?.productTitle || '',
            sku: saddle?.saddlefitSku || '',
            barAngle: saddle?.measurements?.barAngle || '',
            // gulletHeight: saddle?.measurements?.gulletHeight || "",
            gulletWidth: saddle?.measurements?.gulletWidth || '',
            price: saddle?.price || 0,
            discount: saddle?.discount || 0,
            description: saddle?.description || '',
            status: saddle?.status || 'SELL_NOT_LIVE',
          })

          if (saddle.gallery && saddle.gallery.length > 0) {
            const gallery = [...saddle.gallery]
            while (gallery.length < 4) gallery.push(null)
            setSaddleImages(gallery.slice(0, 4))
          }

          if (saddle?.photo) {
            setThumbnail(saddle?.photo)
          }

          if (saddle.scannedFiles?.length > 0) {
            const existingFileUrl = saddle.scannedFiles[0]
            setMeshFile(existingFileUrl)
            const fileName = getFileNameFromUrl(existingFileUrl)
            setMeshFileName(fileName)
          }
        }
      },

      fetchPolicy: 'network-only',
    },
  )

  const baseProductFromDetail = useMemo(() => {
    const rows = data?.listSaddleDetail
    if (!rows?.length) return null
    return (
      rows.find(s => s.type === 'PRODUCT') ||
      rows.find(s => s.sk && !String(s.sk).includes('#variant#')) ||
      rows[0]
    )
  }, [data?.listSaddleDetail])

  useEffect(() => {
    if (openModal && baseProductFromDetail) {
      setValue('variantPrice', baseProductFromDetail?.price)
    }
  }, [openModal, setValue, baseProductFromDetail])

  const existingSeatSizes =
    data?.listSaddleDetail
      ?.filter(
        s =>
          s.type === 'VARIANT' ||
          (s?.sk && String(s.sk).includes('#variant#')),
      )
      ?.map(saddle => saddle?.size)
      .filter(Boolean) || []

  // --- Helper Functions ---
  const isValidImageFile = file => file.type.startsWith('image/')
  const isValidMeshFile = file => {
    const allowedExtensions = ['obj', 'stl', 'mesh']
    const ext = file.name.split('.').pop().toLowerCase()
    return allowedExtensions.includes(ext)
  }

  const getImageUrl = url => {
    if (!url) return null
    const urlString = typeof url === 'string' ? url : url.uri || url.url || null
    if (!urlString) return null
    if (urlString.startsWith('http')) return urlString
    return urlString
  }

  // --- Image Gallery Handlers ---
  const handleCellFileInput = async (e, index) => {
    if (isViewMode) return
    setCellImageErrors(Array(4).fill(''))
    setGalleryTouched(true)
    const file = e.target.files[0]
    if (file) {
      if (!isValidImageFile(file)) {
        setCellImageErrors(prev => {
          const newErrors = [...prev]
          newErrors[index] = 'Invalid image file.'
          return newErrors
        })
        setImageError('Invalid file type. Please upload image files only.')
        return
      }
      setUploadingImages(prev => {
        const newArr = [...prev]
        newArr[index] = true
        return newArr
      })
      try {
        const url = await uploadFile(file)
        setSaddleImages(prev => {
          const newArr = [...prev]
          newArr[index] = url
          return newArr
        })
        setCellImageErrors(prev => {
          const newErrors = [...prev]
          newErrors[index] = ''
          return newErrors
        })
        setImageError('')
      } catch (err) {
        console.error(err)
        setImageError('Error uploading image')
      } finally {
        setUploadingImages(prev => {
          const newArr = [...prev]
          newArr[index] = false
          return newArr
        })
      }
    }
  }

  const handleCellDrop = async (e, index) => {
    if (isViewMode) return
    e.preventDefault()
    setCellImageErrors(Array(4).fill(''))
    setGalleryTouched(true)
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length === 0) return
    let validFile = null
    let invalidFound = false
    for (const file of droppedFiles) {
      if (isValidImageFile(file)) {
        if (!validFile) validFile = file
      } else {
        invalidFound = true
      }
    }
    if (invalidFound) {
      setCellImageErrors(prev => {
        const newErrors = [...prev]
        newErrors[index] = 'Invalid image file.'
        return newErrors
      })
      setImageError('Invalid file. Please drop image files only.')
    } else {
      setCellImageErrors(prev => {
        const newErrors = [...prev]
        newErrors[index] = ''
        return newErrors
      })
      setImageError('')
    }
    if (validFile) {
      setUploadingImages(prev => {
        const newArr = [...prev]
        newArr[index] = true
        return newArr
      })
      try {
        const url = await uploadFile(validFile)
        setSaddleImages(prev => {
          const newArr = [...prev]
          newArr[index] = url
          return newArr
        })
      } catch (err) {
        console.error(err)
        setImageError('Error uploading image')
      } finally {
        setUploadingImages(prev => {
          const newArr = [...prev]
          newArr[index] = false
          return newArr
        })
      }
    }
  }

  const handleRemoveImage = index => {
    if (isViewMode) return
    setSaddleImages(prev => {
      const newArr = [...prev]
      newArr[index] = null
      return newArr
    })
  }

  // --- Thumbnail Handlers ---
  const handleThumbnailUpload = async e => {
    if (isViewMode) return
    setThumbnailTouched(true)
    setThumbnailError('')
    const file = e.target.files[0]
    if (!file) return

    if (!isValidImageFile(file)) {
      setThumbnailError('Invalid image file. Please upload an image.')
      return
    }

    setUploadingThumbnail(true)
    try {
      const url = await uploadFile(file)
      setThumbnail(url)
      setThumbnailError('')
    } catch (err) {
      console.error(err)
      setThumbnailError('Error uploading thumbnail image')
    } finally {
      setUploadingThumbnail(false)
    }
  }

  const handleThumbnailDragOver = e => {
    if (isViewMode) return
    e.preventDefault()
  }

  const handleThumbnailDrop = async e => {
    if (isViewMode) return
    e.preventDefault()
    setThumbnailTouched(true)
    setThumbnailError('')

    const file = e.dataTransfer.files[0]
    if (!file) return

    if (!isValidImageFile(file)) {
      setThumbnailError('Invalid image file. Please drop an image file.')
      return
    }

    setUploadingThumbnail(true)
    try {
      const url = await uploadFile(file)
      setThumbnail(url)
      setThumbnailError('')
    } catch (err) {
      console.error(err)
      setThumbnailError('Error uploading thumbnail image')
    } finally {
      setUploadingThumbnail(false)
    }
  }

  const handleRemoveThumbnail = () => {
    if (isViewMode) return
    setThumbnail(null)
  }

  // --- 3D object file handlers (.obj / .stl / .mesh) ---
  const handleMeshUpload = async e => {
    if (isViewMode) return
    setMeshError('')
    setMeshTouched(true)
    const file = e.target.files[0]
    if (file) {
      if (!isValidMeshFile(file)) {
        setMeshError('Invalid 3D object file type.')
        return
      }
      setUploadingMesh(true)
      try {
        const url = await uploadFile(file, 'mesh', productId)
        setMeshFile(url)
        setMeshFileName(file.name)
        setMeshError('')
      } catch (err) {
        console.error(err)
        setMeshError('Error uploading 3D object file')
      } finally {
        setUploadingMesh(false)
      }
    }
  }

  const handleMeshDragOver = e => {
    if (isViewMode) return
    e.preventDefault()
  }

  const handleMeshDrop = async e => {
    if (isViewMode) return
    e.preventDefault()
    setMeshError('')
    setMeshTouched(true)
    const file = e.dataTransfer.files[0]
    if (file) {
      if (!isValidMeshFile(file)) {
        setMeshError('Invalid 3D object file type.')
        return
      }
      setUploadingMesh(true)
      try {
        const url = await uploadFile(file, 'mesh', productId)
        setMeshFile(url)
        setMeshFileName(file.name)
        setMeshError('')
      } catch (err) {
        console.error(err)
        setMeshError('Error uploading 3D object file')
      } finally {
        setUploadingMesh(false)
      }
    }
  }

  const handleEditVariant = variant => {
    if (isViewMode) return
    setSelectedVariant(variant)
    setOpenEditModal(true)
  }

  const handleDeleteVariant = async variant => {
    if (isViewMode) return
    setCurrentlyDeleting(variant.sk)

    try {
      await deleteSaddleVariant({
        variables: {
          input: {
            pk: variant?.pk,
            sk: variant?.sk,
          },
        },
        refetchQueries: ['SaddleDetailQuery'],
      })

      Toast.fire({
        icon: 'success',
        title: 'Saddle Variant Deleted Successfully!',
      })
    } catch (error) {
      Toast.fire({ icon: 'error', title: error?.message })
    } finally {
      setCurrentlyDeleting(null)
    }
  }

  const handleRemoveMesh = () => {
    if (isViewMode) return
    setMeshFile(null)
    setMeshFileName(null)
  }

  const handleCreateSaddleVariant = async () => {
    if (isViewMode) return
    if (!baseProductFromDetail?.pk || !baseProductFromDetail?.sk) {
      Toast.fire({
        icon: 'error',
        title: 'Product data not loaded. Refresh the page or open the saddle from Saddle Admin.',
      })
      return
    }
    const { size, variantPrice, quantity } = watch()

    const payload = {
      pk: baseProductFromDetail.pk,
      sk: baseProductFromDetail.sk,
      price: parseFloat(variantPrice),
      size: parseFloat(size),
      quantity: parseInt(quantity, 10),
    }

    try {
      await createSaddleVariant({
        variables: { input: payload },
        refetchQueries: ['SaddleDetailQuery'],
      })

      Toast.fire({
        icon: 'success',
        title: 'Saddle Variant Created Successfully!',
      })
      setCancelModal(true)
      setOpenModal(false)
    } catch (error) {
      Toast.fire({ icon: 'error', title: error?.message })
    }
  }

  const handleUpdateSaddleVariant = async () => {
    if (isViewMode) return
    const { size, variantPrice, quantity } = watch()

    const payload = {
      pk: selectedVariant?.pk,
      sk: selectedVariant?.sk,
      price: parseFloat(variantPrice),
      size: parseFloat(size),
      quantity: parseInt(quantity, 10),
    }

    try {
      await updateSaddleVariant({
        variables: { input: payload },
        refetchQueries: ['SaddleDetailQuery'],
      })

      Toast.fire({
        icon: 'success',
        title: 'Saddle Variant Updated Successfully!',
      })
      setOpenEditModal(false)
      setSelectedVariant(null)
    } catch (error) {
      Toast.fire({ icon: 'error', title: error?.message })
    }
  }

  // --- Form Submission ---
  const onSubmit = formData => {
    if (isViewMode) return
    setGalleryTouched(true)
    setMeshTouched(true)
    setThumbnailTouched(true)

    let hasError = false

    // Gallery always required
    const galleryFiles = saddleImages.filter(img => img !== null)
    if (galleryFiles.length === 0) {
      setImageError('Gallery images are required')
      hasError = true
    } else {
      setImageError('')
    }

    // Thumbnail always required
    if (!thumbnail) {
      setThumbnailError('Thumbnail image is required')
      hasError = true
    } else {
      setThumbnailError('')
    }

    // 3D object file required only in create mode
    if (!meshFile && isCreateMode) {
      setMeshError('3D object file is required')
      hasError = true
    } else {
      setMeshError('')
    }

    // Required fields (create + edit), except discipline/sku logic:
    if (!formData.productTitle) hasError = true
    if (!formData.barAngle) hasError = true
    if (!formData.gulletWidth) hasError = true
    if (!formData.price) hasError = true
    if (!formData.description) hasError = true

    // discipline + sku only required in create mode
    if (isCreateMode && !formData.discipline) hasError = true
    if (isCreateMode && !formData.sku) hasError = true

    // discount optional on both
    if (hasError) return

    const currentSaddle =
      isEditMode && baseProductFromDetail ? baseProductFromDetail : null

    const newGallery = saddleImages.filter(img => img !== null)
    const finalGallery = isEditMode
      ? newGallery.length
        ? newGallery
        : currentSaddle?.gallery || []
      : newGallery

    const finalPhoto = thumbnail

    const finalMeshFile = isEditMode
      ? meshFile ??
        currentSaddle?.scannedFile ??
        currentSaddle?.scannedFiles?.[0] ??
        null
      : meshFile

    let payload

    if (isEditMode && currentSaddle) {
      const brandForUpdate =
        currentSaddle.brandName ?? currentSaddle.pk ?? effectiveBrandName
      payload = {
        pk: currentSaddle.pk,
        sk: currentSaddle.sk,
        brandName: brandForUpdate,
        status: formData.status ?? currentSaddle.status,

        price:
          currentSaddle.price !== undefined && currentSaddle.price !== null
            ? currentSaddle.price
            : formData.price,

        productTitle:
          formData.productTitle ?? currentSaddle.productTitle ?? '',

        // discipline optional on edit: if empty string, keep old
        discipline:
          formData.discipline !== undefined && formData.discipline !== ''
            ? formData.discipline
            : currentSaddle.discipline ?? '',

        description:
          formData.description ?? currentSaddle.description ?? '',

        // discount optional: if empty, keep existing or 0
        discount:
          formData.discount !== undefined &&
          formData.discount !== null &&
          formData.discount !== ''
            ? parseFloat(formData.discount)
            : currentSaddle.discount ?? 0,

        // sku optional on edit: if empty string, keep old
        sku:
          formData.sku !== undefined && formData.sku !== ''
            ? formData.sku
            : currentSaddle.saddlefitSku ?? '',

        gallery: finalGallery,
        photo: finalPhoto,
        scannedFile: finalMeshFile,

        gulletWidth:
          formData.gulletWidth !== undefined && formData.gulletWidth !== ''
            ? parseFloat(formData.gulletWidth)
            : currentSaddle.measurements?.gulletWidth ??
              currentSaddle.gulletWidth,

        barAngle:
          formData.barAngle !== undefined && formData.barAngle !== ''
            ? parseFloat(formData.barAngle)
            : currentSaddle.measurements?.barAngle ?? currentSaddle.barAngle,
      }
    } else {
      // CREATE MODE
      payload = {
        productId,
        brandName,
        status: formData.status,
        price: parseFloat(formData.price),
        productTitle: formData.productTitle,
        discipline: formData.discipline,
        gallery: finalGallery,
        photo: finalPhoto,
        description: formData.description,
        // discount optional: if not provided, default 0
        discount:
          formData.discount !== undefined &&
          formData.discount !== null &&
          formData.discount !== ''
            ? parseFloat(formData.discount)
            : 0,
        sku: formData.sku,
        scannedFile: finalMeshFile,
        gulletWidth: parseFloat(formData.gulletWidth),
        // gulletHeight: parseFloat(formData.gulletHeight),
        barAngle: parseFloat(formData.barAngle),
      }
    }

    if (isEditMode) {
      updateSaddle({
        variables: { input: payload },
        refetchQueries: ['SaddlesQuery'],
      })
        .then(() => {
          Toast.fire({ icon: 'success', title: 'Saddle Updated Successfully' })
          navigate('/saddle-admin')
        })
        .catch(err => {
          Toast.fire({ icon: 'error', title: err.message })
        })
    } else {
      createSaddle({
        variables: { input: payload },
        refetchQueries: ['SaddlesQuery'],
      })
        .then(() => {
          Toast.fire({ icon: 'success', title: 'Saddle Created Successfully' })
          navigate('/saddle-admin')
        })
        .catch(err => {
          Toast.fire({ icon: 'error', title: err.message })
        })
    }
  }

  const onError = () => {
    setGalleryTouched(true)
    setMeshTouched(true)
    setThumbnailTouched(true)

    const galleryFiles = saddleImages.filter(img => img !== null)
    if (galleryFiles.length === 0) {
      setImageError('Gallery images are required')
    } else {
      setImageError('')
    }

    if (!thumbnail) {
      setThumbnailError('Thumbnail image is required')
    } else {
      setThumbnailError('')
    }

    if (!meshFile && isCreateMode) {
      setMeshError('3D object file is required')
    } else {
      setMeshError('')
    }
  }

  const isUploading =
    uploadingMesh ||
    uploadingThumbnail ||
    uploadingImages.some(uploading => uploading)

  const disciplineValue = methods.watch('discipline')
  const selectedDisciplineOption =
    westernOptions[0]?.options?.find(opt => opt.value === disciplineValue) ||
    null

  function getFileNameFromUrl(url) {
    if (!url) return ''
    try {
      const lastSlashIndex = url.lastIndexOf('/')
      if (lastSlashIndex === -1) return url
      return decodeURIComponent(url.substring(lastSlashIndex + 1))
    } catch (err) {
      return url
    }
  }

  if (saddleDetailsLoading) {
    return (
      <div className="loader-container !w-[260px] h-full flex justify-center items-center">
        <BeatLoader color={'#2B364B'} size={20} />
      </div>
    )
  }

  const renderForm = () => (
    <>
      {/* Discipline, Product Title, and SKU */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <FormInput
          name="discipline"
          type="select2"
          label="Discipline"
          isDisabled={isViewMode}
          height={52}
          placeholder="Select Discipline"
          options={westernOptions}
          selectedValues={selectedDisciplineOption}
          validations={{
            required: {
              value: isCreateMode, // create: required, edit: optional
              message: 'Discipline is required',
            },
          }}
          onChange={option => {
            if (!isViewMode) {
              setValue('discipline', option?.value)
              clearErrors('discipline')
            }
          }}
        />

        <FormInput
          name="productTitle"
          label="Product Title"
          placeholder="Enter product title"
          disabled={isViewMode}
          validations={{
            required: {
              value: true,
              message: 'Product Title is required',
            },
          }}
        />
        <FormInput
          name="sku"
          label="SKU"
          placeholder="Enter SKU"
          disabled={isViewMode}
          validations={{
            required: {
              value: isCreateMode, // create: required, edit: optional
              message: 'SKU is required',
            },
          }}
        />
      </div>

      {/* Measurements */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <FormInput
          name="barAngle"
          label="Bar Angle"
          type="number"
          step="any"
          placeholder="Enter bar angle"
          disabled={isViewMode}
          validations={{
            required: {
              value: true,
              message: 'Bar Angle is required',
            },
            min: {
              value: 0.0000001,
              message: 'Bar Angle must be greater than 0',
            },
          }}
        />

        <FormInput
          name="gulletWidth"
          label="Gullet Width"
          type="number"
          step="any"
          placeholder="Enter gullet width"
          disabled={isViewMode}
          validations={{
            required: {
              value: true,
              message: 'Gullet Width is required',
            },
            min: {
              value: 0.0000001,
              message: 'Gullet Width must be greater than 0',
            },
          }}
          onInput={e => {
            if (e.target.value < 0) e.target.value = ''
          }}
          onKeyDown={e => {
            if (['-', 'e', 'E'].includes(e.key)) {
              e.preventDefault()
            }
          }}
        />
        <FormInput
          name="price"
          label="Price"
          type="number"
          step="0.01"
          placeholder="Enter price"
          disabled={isViewMode || isEditMode}
          validations={{
            required: {
              value: true,
              message: 'Price is required',
            },
            min: { value: 1500, message: 'Price must be at least 1,500' },
            max: { value: 10000, message: 'Price must not exceed 10,000' },
          }}
          onInput={e => {
            if (e.target.value < 0) e.target.value = ''
          }}
          onKeyDown={e => {
            if (['-', 'e', 'E'].includes(e.key)) {
              e.preventDefault()
            }
          }}
        />
      </div>

      {/* Price, Discount, and Status Toggle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <FormInput
          name="discount"
          label="Discount"
          type="number"
          step="0.01"
          placeholder="Enter discount"
          disabled={isViewMode}
          validations={{
            min: { value: 0, message: 'Discount must be non-negative' },
            max: { value: 10000, message: 'Discount must not exceed 10,000' },
          }}
          onInput={e => {
            if (e.target.value < 0) e.target.value = ''
          }}
          onKeyDown={e => {
            if (['-', 'e', 'E'].includes(e.key)) {
              e.preventDefault()
            }
          }}
        />

        {isEditMode && (
          <div className="flex flex-col">
            <label className="block text-[14px] sm:text-[18px] font-montserrat font-medium text-gray-700 mb-2">
              Status
            </label>
            <label
              className={`relative inline-flex items-center ${
                isViewMode ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only peer"
                checked={watch('status') === 'SELL_LIVE'}
                disabled={isViewMode}
                onChange={e => {
                  if (isViewMode) return
                  setValue(
                    'status',
                    e.target.checked ? 'SELL_LIVE' : 'SELL_NOT_LIVE',
                  )
                }}
              />
              <div
                className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
              peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-700 
              peer-checked:after:translate-x-full peer-checked:after:border-white 
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-white after:border-gray-300 after:border after:rounded-full 
              after:h-5 after:w-5 after:transition-all dark:border-gray-600 
              peer-checked:bg-[#2B364B]"
              />
              <span className="ml-3 text-sm font-medium font-montserrat text-gray-900">
                {watch('status') === 'SELL_LIVE' ? 'Live' : 'Disabled'}
              </span>
            </label>
          </div>
        )}
      </div>

      <FormRichTextEditor
        name="description"
        label="Description"
        placeholder="Enter product description..."
        disabled={isViewMode}
        validations={{
          required: {
            value: true,
            message: 'Description is required',
          },
        }}
      />

      {/* Thumbnail Upload */}
      <div className="mt-8">
        <label className="block text-[14px] sm:text-[18px] font-montserrat font-medium text-gray-700 mb-4">
          Thumbnail Image
        </label>
        <div
          onDragOver={e => {
            if (!isViewMode) handleThumbnailDragOver(e)
          }}
          onDrop={e => {
            if (!isViewMode) handleThumbnailDrop(e)
          }}
          className={`relative h-[120px] w-[160px] rounded-lg border-2 border-dashed flex items-center justify-center ${
            thumbnailError ? 'border-red-500' : 'border-gray-300'
          } ${
            isUploading ? 'cursor-not-allowed' : 'cursor-pointer'
          } hover:bg-gray-50`}
        >
          {thumbnail ? (
            <div className="w-full h-full relative">
              <img
                src={getImageUrl(thumbnail)}
                alt="Thumbnail"
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                type="button"
                disabled={
                  isViewMode || loading || updateSaddleLoading || isUploading
                }
                onClick={handleRemoveThumbnail}
                className={`absolute top-0 right-0 p-1 ${
                  isViewMode || isUploading || loading || updateSaddleLoading
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                } bg-[#2B364B] rounded-full text-white flex items-center justify-center`}
              >
                <CloseIcon />
              </button>
            </div>
          ) : (
            <label
              className={`w-full h-full flex flex-col items-center justify-center ${
                isViewMode || isUploading || loading || updateSaddleLoading
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleThumbnailUpload}
                disabled={isViewMode || uploadingThumbnail || isUploading}
              />
              {uploadingThumbnail ? (
                <span className="text-gray-400 text-xs font-montserrat">
                  Uploading…
                </span>
              ) : (
                <ImagePlus className="h-8 w-8 text-gray-400" />
              )}
            </label>
          )}
        </div>
        {thumbnailTouched && thumbnailError && (
          <p className="mt-2 font-montserrat text-sm text-red-500">
            {thumbnailError}
          </p>
        )}
      </div>

      {/* Gallery Images */}
      <div className="mt-8">
        <label className="block text-[14px] sm:text-[18px] font-montserrat font-medium text-gray-700 mb-4">
          Gallery Images (Max 4)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              onDragOver={e => {
                if (!isViewMode) e.preventDefault()
              }}
              onDrop={e => {
                if (!isViewMode) handleCellDrop(e, index)
              }}
              className={`relative h-[90px] w-[140px] rounded-lg border-2 border-dashed flex items-center justify-center ${
                cellImageErrors[index] ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {saddleImages[index] ? (
                <div className="max-w-[140px] w-full h-[90px]">
                  <img
                    src={saddleImages[index]}
                    alt={`Gallery image ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    disabled={
                      isViewMode ||
                      loading ||
                      updateSaddleLoading ||
                      isUploading
                    }
                    onClick={() => handleRemoveImage(index)}
                    className={`absolute top-0 right-0 p-1 ${
                      isViewMode ||
                      isUploading ||
                      loading ||
                      updateSaddleLoading
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer'
                    } bg-[#2B364B] rounded-full text-white flex items-center justify-center`}
                  >
                    <CloseIcon />
                  </button>
                </div>
              ) : (
                <label
                  className={`w-[140px] h-full flex flex-col items-center justify-center ${
                    isViewMode || isUploading || loading || updateSaddleLoading
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer'
                  } hover:bg-gray-50`}
                >
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={e => {
                      if (!isViewMode) handleCellFileInput(e, index)
                    }}
                    disabled={
                      isViewMode ||
                      uploadingImages[index] ||
                      uploadingMesh ||
                      uploadingThumbnail
                    }
                  />
                  {uploadingImages[index] ? (
                    <span className="text-gray-400 text-xs font-montserrat">
                      Uploading…
                    </span>
                  ) : (
                    <ImagePlus className="h-8 w-8 text-gray-400" />
                  )}
                </label>
              )}
            </div>
          ))}
        </div>
        {galleryTouched && imageError && (
          <p className="mt-2 font-montserrat text-sm text-red-500">
            {imageError}
          </p>
        )}
      </div>

      {/* 3D object file upload */}
      <div className="mt-8">
        <h3 className="block text-[14px] sm:text-[18px] font-montserrat font-medium text-gray-700 mb-4">
          3D object file
        </h3>
        <div
          onDragOver={e => {
            if (!isViewMode) handleMeshDragOver(e)
          }}
          onDrop={e => {
            if (!isViewMode) handleMeshDrop(e)
          }}
          className={`relative border-2 border-dashed rounded p-4 text-center ${
            isUploading ? 'cursor-not-allowed' : 'cursor-pointer'
          } hover:bg-gray-50 ${
            meshError ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <input
            type="file"
            accept=".obj,.stl,.mesh"
            onChange={handleMeshUpload}
            className="hidden"
            id="mesh-upload"
            disabled={isViewMode || isUploading}
          />
          {uploadingMesh ? (
            <span className="text-gray-400 text-xs font-montserrat">
              Uploading…
            </span>
          ) : meshFile ? (
            <div className="relative">
              <p className="font-montserrat text-sm text-gray-600">
                {getFileNameFromUrl(meshFile)}
              </p>
              <button
                type="button"
                onClick={handleRemoveMesh}
                disabled={
                  isViewMode || loading || updateSaddleLoading || isUploading
                }
                className={`absolute top-[-10px] right-0 p-1 ${
                  isViewMode || isUploading || loading || updateSaddleLoading
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                } bg-[#2B364B] rounded-full text-white flex items-center justify-center`}
              >
                <CloseIcon />
              </button>
            </div>
          ) : (
            <label
              htmlFor="mesh-upload"
              className={`font-montserrat ${
                isViewMode || isUploading || loading || updateSaddleLoading
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer'
              } block`}
            >
              {isViewMode
                ? '3D object file'
                : 'Drag & drop or click to upload (.obj, .stl, .mesh)'}
            </label>
          )}
        </div>
        {meshTouched && meshError && (
          <p className="mt-2 text-sm text-red-500 font-montserrat">
            {meshError}
          </p>
        )}
      </div>
    </>
  )

  if (saddleDetailsLoading) {
    return (
      <div className="loader-container !w-[260px] h-full flex justify-center items-center">
        <BeatLoader color={'#2B364B'} size={20} />
      </div>
    )
  }

  if (
    isEditMode &&
    !saddleDetailsLoading &&
    (saddleDetailError || !data?.listSaddleDetail?.length)
  ) {
    return (
      <div className="w-full max-w-xl mx-auto px-4 py-8 text-center">
        <p className="text-red-600 mb-2 font-montserrat">
          Could not load this saddle. Open it from Saddle Admin or check the
          link.
        </p>
        {saddleDetailError?.message && (
          <p className="text-sm text-gray-600 mb-4 font-montserrat">
            {saddleDetailError.message}
          </p>
        )}
        <Button type="button" onClick={() => navigate('/saddle-admin')}>
          Back to saddle list
        </Button>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {renderForm()}
        {isEditMode && data?.listSaddleDetail && (
          <div className="mt-8 w-full max-w-5xl mx-auto">
            <div className="flex flex-col items-center mb-4">
              <h3 className="block sm:text-[30px] font-barlowcondensed font-bold text-[#6B7280] mb-4">
                Seat Sizes
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {data?.listSaddleDetail
                  ?.map(saddle => saddle.size)
                  .filter(Boolean)
                  .map((size, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gray-300 text-[#6B7280] rounded-full text-sm font-semibold font-montserrat"
                    >
                      {size}
                    </span>
                  ))}
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (!isViewMode) setOpenModal(true)
                }}
                className={`create-saddle-btn !w-[200px] ${
                  isUploading || loading || updateSaddleLoading
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
                disabled={
                  isViewMode || loading || updateSaddleLoading || isUploading
                }
              >
                Add Variant
              </Button>
            </div>
            <VariantsTable
              saddles={data?.listSaddleDetail}
              sortConfig={sortConfig}
              handleSort={handleSort}
              currentlyDeleting={currentlyDeleting}
              deleteSaddleLoading={deleteSaddleLoading}
              onEdit={
                !isViewMode ? variant => handleEditVariant(variant) : undefined
              }
              onDelete={
                !isViewMode
                  ? variant => handleDeleteVariant(variant)
                  : undefined
              }
            />
          </div>
        )}
        <div className="flex flex-wrap justify-center sm:justify-end gap-4 mt-10 mb-4">
          <Button
            type="button"
            primaryOutline
            onClick={() => navigate(-1)}
            className={`create-saddle-cancel-btn !w-[130px] ${
              isUploading || loading || updateSaddleLoading
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }`}
            disabled={
              loading ||
              updateSaddleLoading ||
              updateSaddleLoading ||
              isUploading
            }
          >
            Cancel
          </Button>
          <Button
            loading={loading || updateSaddleLoading}
            type="submit"
            className={`create-saddle-btn !w-[150px] ${
              isUploading || loading || updateSaddleLoading
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }`}
            disabled={
              loading ||
              updateSaddleLoading ||
              updateSaddleLoading ||
              isUploading
            }
          >
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>

      {(openModal || openEditModal) && !isViewMode && (
        <ModalComponent
          title={selectedVariant ? 'Update Variant' : 'Create Variant'}
          children={
            <form
              id="variant-form"
              onSubmit={
                selectedVariant
                  ? handleSubmit(handleUpdateSaddleVariant)
                  : handleSubmit(handleCreateSaddleVariant)
              }
            >
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-3xl mx-auto">
                  <FormInput
                    name="size"
                    type="single-select"
                    label="Seat Size"
                    height={52}
                    placeholder="Select Size"
                    selectedValues={selectedSeatSizeOption}
                    options={seatSizes.filter(
                      option => !existingSeatSizes.includes(option.value),
                    )}
                    validations={{
                      required: {
                        value: true,
                        message: 'Seat size is required',
                      },
                    }}
                    onChange={option => {
                      setValue('size', option?.value)
                      clearErrors('size')
                    }}
                  />

                  <FormInput
                    name="variantPrice"
                    label="Price"
                    type="number"
                    step="0.01"
                    placeholder="Enter price"
                    validations={{
                      required: { value: true, message: 'Price is required' },
                      min: {
                        value: 1500,
                        message: 'Price must be at least 1,500',
                      },
                      max: {
                        value: 10000,
                        message: 'Price must not exceed 10,000',
                      },
                    }}
                  />
                </div>

                <FormInput
                  name="quantity"
                  label="Quantity"
                  type="number"
                  min="1"
                  placeholder="Enter quantity"
                  validations={{
                    required: { value: true, message: 'Quantity is required' },
                    min: {
                      value: 1,
                      message: 'Quantity must be greater than 0',
                    },
                  }}
                />
              </div>
            </form>
          }
          cancelModal={cancelModal}
          setCancelModal={setCancelModal}
          setOpenModal={setOpenModal}
          openEditModal={openEditModal}
          setOpenEditModal={setOpenEditModal}
          setSelectedVariant={setSelectedVariant}
          createLoading={createSaddleVariantLoading}
          updateLoading={updateSaddleVariantLoading}
          onCreate={() =>
            document.getElementById('variant-form').requestSubmit()
          }
          onUpdate={() =>
            document.getElementById('variant-form').requestSubmit()
          }
        />
      )}
    </FormProvider>
  )
}

export default SaddleForm
